import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import SwiperContainer from './components/swiperContainer'
import { initialReducerState } from './constants/index'
import { getRap, reducer, useMultiplyRefs } from './utils'
import Keyboard from './components/keyboard'
import { GetAttr } from '../../js/utils/getAttr'
import { makeRequest } from '../../js/utils/makeRequest'
import { getFormDataFromObj } from '../../js/utils/getFormDataFromObj'
import { errorMessage } from '../calculator/constants'
import { debounce } from '../../js/utils/debounce'

const App = ({ data, rap, initialTabs, claritySort = ["FL","IF","VVS1","VVS2","VS1","VS2","SI1","SI2","SI3","I1","I2","I3"], urlSaveValues, urlGetValues, auxParams }) => {
  const [state, dispatch] = useReducer(reducer, initialReducerState)
  const [compareValues, setCompareValues] = useState([])
  const [savedData, setSavedData] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const stateClasses = {
    active: 'is-active'
  }

  const callData = useMemo(() => {
    return {
      rap: getRap(rap),
      colors: [...new Set(data.map(item => item.color))],
      clarity: [...new Set(data.map(item => item.clarity))].sort((a, b) => claritySort.indexOf(a) - claritySort.indexOf(b)),
    }
  }, [])

  const onControlChange = (type, value) => {
    dispatch({ type, payload: value })
  }

  const findClosest = (arr, num) => {
    if (arr == null) {
      return
    }

    let closest = arr[0]
    for (let item of arr) {
      if (Math.abs(item - num) < Math.abs(closest - num)) {
        closest = item
      }
    }
    return closest
  }

  const reset = () => {
    setCompareValues([])
    dispatch({ type: 'resetAllInputs' })
    swiperRefs.forEach(({ current }) => {
      current.slideTo(current.el.dataset.jsInitialslide ?? current.passedParams.initialSlide)
    })
  }

  useEffect(() => {
    if (auxParams && urlGetValues) {
      setIsLoading(true)
      const savedValues = async () => {
        return makeRequest({ url: urlGetValues, data: getFormDataFromObj(auxParams), method: "POST" })
          .then(({ success, data, errors }) => {
            if (success) {
              return data
            } else {
              return Promise.reject(errors)
            }
          })
      }
      savedValues()
        .then((data) => {
          setSavedData(() => ({...data, isRound: String(data.isRound), rap: data.rap.replace(',', '.')}))
          const { carat, isRound, color, clarity, rap } = data
          if (carat) {
            dispatch({ type: 'carat', payload: carat })
          }
          swiperRefs.forEach(({ current }) => {
            if (current.params.group === 'shape' && isRound !== undefined) {
              current.slideTo(Array.from(current.slides).indexOf(Array.from(current.slides).find(slide => slide.dataset.jsValue === String(isRound))))
              dispatch({ type: 'shape', payload: String(isRound) })
            } else if (current.params.group === 'color' && color) {
              current.slideTo(Array.from(current.slides).indexOf(Array.from(current.slides).find(slide => slide.dataset.jsValue === color)))
              dispatch({ type: 'color', payload: color })
            } else if (current.params.group === 'clarity' && clarity) {
              current.slideTo(Array.from(current.slides).indexOf(Array.from(current.slides).find(slide => slide.dataset.jsValue === clarity)))
              dispatch({ type: 'clarity', payload: clarity })
            } else if (current.params.group === 'rap' && rap) {
              current.slideTo(Array.from(current.slides).indexOf(Array.from(current.slides).find(slide => slide.dataset.jsValue === rap.replace(',', '.'))))
              dispatch({ type: 'rap', payload: rap })
            }
          })
        })
        .catch((e) => {
          console.error(e)
          setSavedData({
            carat: state.carat,
            isRound: state.shape,
            color: state.color,
            clarity: state.clarity,
            rap: state.rap
          })
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [])

  useEffect(() => {
    if(state.activeInputName === 'carat') {
      if (state.carat.length) {
        const find = data.filter((item) => {
          return (item.color === state.color && item.clarity === state.clarity && String(item.is_round) === state.shape)
        })
        const minMaxValues = [...new Set(find.map(item => item.min)), ...new Set(find.map(item => item.max))]
        const closestValue = findClosest(minMaxValues, +state.carat)
        const obj = find.filter((item) => item.min === closestValue || item.max === closestValue)
        if (Number(state.carat.replace(',', '.'))) {
          dispatch({ type: 'price', payload: +obj[0].price + +(obj[0].price / 100) * state.rap})
          dispatch({ type: 'priceInitial', payload: +obj[0].price })
        } else {
          dispatch({ type: 'price', payload: 0 })
          dispatch({ type: 'priceInitial', payload: 0 })
        }
      } else {
        dispatch({ type: 'resetAllInputs' })
      }
    }
  }, [state.carat, state.color, state.clarity, state.shape, state.rap])

  useEffect(() => {
    if(state.activeInputName !== 'priceTotal') {
      dispatch({type: 'priceTotal', payload: Math.round(state.carat * state.price)})
    }
    dispatch({ type: 'priceTotalInitial', payload: Math.round(state.carat * state.priceInitial)})
  }, [state.price, state.carat])

  useEffect(() => {
    if(state.activeInputName !== 'carat' && state.priceInitial !== '0' && state.priceTotalInitial !== '0') {
      const rap = (state.price * 100 / state.priceInitial) - 100

      const decimal = rap % 1 === 0 ? 0 : 1;
      const roundedRap = Number((Math.round(rap * 2) / 2).toFixed(decimal))

      slideRapSwiperToRapValue(roundedRap)
    }
  }, [state.price, state.priceTotal])

  useEffect(() => {
    if(state.activeInputName === 'priceTotal') {
      if(Number(state.priceTotal) !== 0 && Number(state.carat) !== 0) {
        dispatch({type: 'price', payload: Math.round(state.priceTotal / state.carat)})
      }
    }
  }, [state.priceTotal])


  const saveValues = () => {
    if (state.price) {
      setCompareValues((prevState) => [{ price: state.price, priceTotal: state.priceTotal }, ...prevState])
    }
  }

  const slides = useMemo(() => {
    return [
      {
        values: initialTabs,
        title: 'Форма',
        group: 'shape',
        initialSlide: initialTabs.findIndex(tab => tab.isActive)
      },
      {
        values: callData.colors,
        title: 'Цвет',
        group: 'color'
      },
      {
        values: callData.clarity,
        title: 'Чистота',
        group: 'clarity',
      },
      {
        values: callData.rap,
        title: '%RAP',
        group: 'rap',
        initialSlide: callData.rap.indexOf(rap.initialValue),
        isRap: true
      }
    ]
  }, [])

  const swiperRefs = useMultiplyRefs(4)

  const inputRefs = useMultiplyRefs(3)

  const refBody = useRef(null)

  const handleInputFocus = (e) => {
    inputRefs.forEach((input) => {
      input.current.classList.toggle(stateClasses.active, input.current.contains(e.target))
    })
    dispatch({ type: 'activeInputName', payload: e.target.name})
  }

  const slideRapSwiperToRapValue = (rapValue) => {
    const rapSwiper = swiperRefs.find((item) => item.current?.params.isRap)?.current

    if(rapValue > 100) {
      rapSwiper?.slideTo(Array.from(rapSwiper?.slides).length - 1)
    } else {
      const slideIndexNeeded = Array.from(rapSwiper?.slides).findIndex((slide) => {
        return Number(slide.getAttribute(GetAttr('[data-js-value]'))) === rapValue
      })
      rapSwiper?.slideTo(slideIndexNeeded)
    }
  }

  const saveFilter = () => {
    const body = {
      ...auxParams,
      ...refBody.current
    }
    makeRequest({ url: urlSaveValues, data: getFormDataFromObj(body), method: "POST" })
      .then(({ success, error = errorMessage }) => {
        if (!success) {
          return Promise.reject(error)
        }
      })
      .catch((e) => {
        console.error(e)
      })
  }

  const saveFilterDebounce = useCallback(debounce(saveFilter, 3000), [])

  useEffect(() => {
    if (
      !isLoading &&
      Object.keys(savedData).length &&
      (
        state.color !== savedData.color ||
        state.clarity !== savedData.clarity ||
        state.shape !== savedData.isRound ||
        state.rap !== savedData.rap ||
        state.carat !== savedData.carat
      )
    ) {
      setSavedData({
        carat: state.carat,
        isRound: state.shape,
        color: state.color,
        clarity: state.clarity,
        rap: state.rap
      })
      refBody.current = {
        color: state.color,
        clarity: state.clarity,
        shape: state.shape,
        rap: state.rap,
        carat: state.carat
      }
      saveFilterDebounce()
    }
  }, [state.color, state.clarity, state.shape, state.rap, state.carat])

  return (
    <div className="calculator-ma__container">
      <p className="calculator-ma__input-label">Карат</p>
      <input className="calculator-ma__carat calculator-ma__input is-active" onFocus={handleInputFocus} name={'carat'} value={state.carat.toString().replace('.', ',')} readOnly={true} ref={inputRefs[0]} />
      <div className="calculator-ma__rollers">
        {slides.map((slide, index) => {
          return <SwiperContainer key={index} {...slide} slides={3}
                                  onControlChange={onControlChange}
                                  ref={swiperRefs[index]}/>
        })}
      </div>
      <div className="calculator-ma__price-container">
        <div className="calculator-ma__prices-wrapper">
          <div className="calculator-ma__prices calculator-ma__prices--rap">
            <div className="calculator-ma__price">
              <p className="calculator-ma__price-title">Rap price CT</p>
              <p className='calculator-ma__price-count'>$ {state.priceInitial.toString().replace('.', ',')}</p>
            </div>
            <div className="calculator-ma__price">
              <p className="calculator-ma__price-title">Rap price 0%</p>
              <p className='calculator-ma__price-count'>$ {state.priceTotalInitial.toString().replace('.', ',')}</p>
            </div>
          </div>

          <div className="calculator-ma__prices">
            <div className="calculator-ma__price">
              <p className="calculator-ma__price-title">Цена \ Ct</p>
              <input className="calculator-ma__price-count calculator-ma__input" onFocus={handleInputFocus} name={'price'} value={`$${state.price.toString().replace('.', ',')}`} readOnly={true} ref={inputRefs[1]} />
            </div>
            <div className="calculator-ma__price">
              <p className="calculator-ma__price-title">Итого</p>
              <input className="calculator-ma__price-count calculator-ma__input" onFocus={handleInputFocus} name={'priceTotal'} value={`$${state.priceTotal.toString().replace('.', ',')}`} readOnly={true} ref={inputRefs[2]} />
            </div>
          </div>
        </div>

        <div className="calculator-ma__compares">
          {compareValues.length ? compareValues.map((item, index) => {
            return <div className="calculator-ma__compare-item" key={index}>
              <div className="calculator-ma__prices">
                <div className="calculator-ma__price">
                  <p className="calculator-ma__price-count">${item.price}</p>
                </div>
                <div className="calculator-ma__price">
                  <p className="calculator-ma__price-count">${item.priceTotal}</p>
                </div>
              </div>
            </div>
          }) : ''}
        </div>
      </div>
      <div className="calculator-ma__calc-items">
        <Keyboard dispatch={dispatch} state={state}/>
        <div className="calculator-ma__control">
          <button onClick={reset} className="calculator-ma__btn calculator-ma__btn_reset">Сбросить</button>
          <button onClick={saveValues} className="calculator-ma__btn calculator-ma__btn_compare">Сравнить</button>
        </div>
      </div>
    </div>
  )
}

export default App
